import { z } from 'zod';
/**
 * Request schemas, enums and labels of the calendar (Beratung 01, 04, 07, 09) and the public booking page
 * (Buchungsseite 01–10) shared by the API (validation) and the SPA (forms, chips, tabs). German strings
 * verbatim from the screens; identifiers English (docs/glossary.md). Response shapes: docs/dev/api-appointments.md
 * (calendar) and docs/dev/api-booking.md (public page).
 */
const dayKey = z.iso.date('Bitte ein Datum im Format JJJJ-MM-TT angeben.');
const instant = z.iso.datetime({ offset: true, message: 'Bitte einen Zeitpunkt angeben.' });
// --- calendar (Beratung 01, 04, 09) ------------------------------------------------------------------
export const calendarWeekQuerySchema = z.object({
    /** Any day of the week; the week starts on its Monday (Europe/Berlin). Default: the current week. */
    start: dayKey.optional(),
    userId: z.string().uuid().optional(),
});
export const archiveRanges = ['30d', '90d', 'all'];
/** Filter pill of Beratung 04 („Letzte 30 Tage"). */
export const archiveRangeLabels = {
    '30d': 'Letzte 30 Tage',
    '90d': 'Letzte 90 Tage',
    all: 'Gesamter Zeitraum',
};
export const calendarArchiveQuerySchema = z.object({
    advisorId: z.string().uuid().optional(),
    range: z.enum(archiveRanges).default('30d'),
    cursor: z.string().max(200).optional(),
});
export const calendarStatisticsPeriods = ['7d', '30d', '90d', '1y'];
/** Period tabs of Beratung 09. */
export const calendarStatisticsPeriodLabels = {
    '7d': '7 Tage',
    '30d': '30 Tage',
    '90d': '90 Tage',
    '1y': '1 Jahr',
};
export const calendarStatisticsQuerySchema = z.object({
    period: z.enum(calendarStatisticsPeriods).default('30d'),
});
// --- reminder chain (Beratung 07) ---------------------------------------------------------------------
export const reminderChainChannels = ['confirmation', 'email', 'whatsapp', 'sms', 'appointment'];
export const reminderChainTimings = [
    'at_booking',
    'before_appointment',
    'after_appointment',
    'at_start',
];
export const reminderChainOffsetUnits = ['minutes', 'hours', 'days'];
/** Step tile labels (Beratung 07: „Bestätigung", „WhatsApp", „E-Mail", „Termin"). */
export const reminderChainChannelLabels = {
    confirmation: 'Bestätigung',
    email: 'E-Mail',
    whatsapp: 'WhatsApp',
    sms: 'SMS',
    appointment: 'Termin',
};
/** Options of the „Zeitpunkt" select (Beratung 07 „Vor dem Termin"). */
export const reminderChainTimingLabels = {
    at_booking: 'Bei Buchung',
    before_appointment: 'Vor dem Termin',
    after_appointment: 'Nach dem Termin',
    at_start: 'Beginn',
};
/** Options of the „Abstand" unit select (Beratung 07 „Std"). */
export const reminderChainOffsetUnitLabels = {
    minutes: 'Min',
    hours: 'Std',
    days: 'Tage',
};
/** The eight text variables of the step editor (Beratung 07 hint). */
export const reminderPlaceholders = [
    'gast_vorname',
    'ereignis',
    'datum',
    'uhrzeit',
    'berater',
    'meet_link',
    'objekt',
    'einheit',
];
export const REMINDER_TEXT_MAX = 1000;
export const reminderChainStepInputSchema = z.object({
    /** Id of an existing step of this chain (kept and updated); omitted or foreign → a new step. */
    id: z.string().uuid().optional(),
    channel: z.enum(reminderChainChannels),
    timing: z.enum(reminderChainTimings),
    offsetValue: z.number().int().min(0).max(1000),
    offsetUnit: z.enum(reminderChainOffsetUnits),
    /** null = „Zurück zum Standardtext" (the organization's text for this channel). */
    text: z.string().max(4000).nullable(),
});
export const putReminderChainBodySchema = z.object({
    steps: z.array(reminderChainStepInputSchema).min(2).max(20),
});
// --- public booking page (Buchungsseite 01–10) --------------------------------------------------------
export const BOOKING_EMAIL_MESSAGE = 'Bitte prüfen Sie die E-Mail-Adresse.';
export const BOOKING_CONSENT_MESSAGE = 'Bitte bestätigen Sie die Einwilligung.';
/** „Anrede" of the booking page (D-454). */
export const bookingSalutations = ['Herr', 'Frau'];
export const BOOKING_SALUTATION_MESSAGE = 'Bitte wählen Sie Herr oder Frau.';
export const bookingCancelReasons = ['time', 'no_interest', 'found_other', 'other'];
/** Reason chips of Buchungsseite 07. */
export const bookingCancelReasonLabels = {
    time: 'Zeitlich passt es nicht',
    no_interest: 'Kein Interesse mehr',
    found_other: 'Schon etwas gefunden',
    other: 'Sonstiges',
};
const personName = z.string().trim().min(1, 'Pflichtangabe.').max(100);
const bookingEmail = z.string().trim().max(254).email(BOOKING_EMAIL_MESSAGE);
const bookingPhone = z.string().trim().min(1, 'Pflichtangabe.').max(40);
const consent = z.literal(true, BOOKING_CONSENT_MESSAGE);
export const bookingMonthQuerySchema = z.object({
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Bitte einen Monat im Format JJJJ-MM angeben.'),
});
export const bookingTimesQuerySchema = z.object({ date: dayKey });
export const bookingHoldBodySchema = z.object({
    startsAt: instant,
    /** The key of the customer's previous hold — it is released when the new one is taken. */
    holdKey: z.string().min(1).max(400).optional(),
});
export const bookingBookBodySchema = z.object({
    holdKey: z.string().min(1).max(400),
    firstName: personName,
    lastName: personName,
    email: bookingEmail,
    phone: bookingPhone,
    concern: z.string().trim().max(2000).optional(),
    /**
     * Setter booking (only event types with a customer sequence, D-375): „Gebucht durch (Setter)" and the
     * „Gesprächsnotiz" (required when a setter is named; the API answers `fields.setterNote` „Pflichtangabe.").
     */
    setterName: z.string().trim().max(100).optional(),
    setterNote: z.string().trim().max(2000).optional(),
    /**
     * „Anrede" (D-454): required with a setter (`fields.salutation` „Pflichtangabe."), optional otherwise; stored on the
     * contact and the source of the sequence's „Herr/Frau <Nachname>".
     */
    salutation: z.enum(bookingSalutations, BOOKING_SALUTATION_MESSAGE).optional(),
    consent,
});
export const bookingRescheduleBodySchema = z.object({ holdKey: z.string().min(1).max(400) });
export const bookingCancelBodySchema = z.object({ reason: z.enum(bookingCancelReasons).optional() });
export const bookingCallbackBodySchema = z.object({
    firstName: personName,
    lastName: personName,
    phone: bookingPhone,
    email: bookingEmail.optional(),
    consent,
});
//# sourceMappingURL=booking.js.map